// Grid used on the main page to display up to six quick-pick materials (recents/favorites/featured).
import React from "react";
import { Matiere } from "../types";

interface RecentMaterialsGridProps {
  materials: Matiere[];
  featuredMaterial?: Matiere;
  selectedMaterialId?: string;
  onSelect: (id: string) => void;
  onOpenCatalog: () => void;
}

const formatPriceTTC = (matiere: Matiere) => (matiere.prix * 1.2).toFixed(2);

export const RecentMaterialsGrid: React.FC<RecentMaterialsGridProps> = ({
  materials,
  featuredMaterial,
  selectedMaterialId,
  onSelect,
  onOpenCatalog,
}) => {
  const hasFeatured = featuredMaterial && !materials.some((m) => m.id === featuredMaterial.id);
  const tiles = hasFeatured ? [featuredMaterial!, ...materials] : materials;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-md font-semibold text-gray-900">Sélection rapide</h3>
          <p className="text-sm text-gray-500">Jusqu'à 6 matières récentes ou favorites</p>
        </div>
        <button
          type="button"
        onClick={onOpenCatalog}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100"
      >
          <span className="text-lg" aria-hidden>
            +
          </span>
          Choisir ou créer une matière
        </button>
      </div>

      {tiles.length === 0 ? (
        <div className="border border-dashed border-gray-300 rounded-lg p-4 text-sm text-gray-600 bg-gray-50">
          Aucune matière récente ou favorite. Ouvrez le catalogue pour choisir.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {tiles.map((matiere) => {
            const isSelected = selectedMaterialId === matiere.id;
            const isFeatured = featuredMaterial?.id === matiere.id;
            return (
              <button
                key={matiere.id}
                type="button"
                onClick={() => onSelect(matiere.id)}
                className={`relative text-left border rounded-lg p-4 transition-all hover:shadow ${
                  isSelected ? "border-blue-500 ring-2 ring-blue-500 ring-opacity-40 bg-blue-50" : "border-gray-200 bg-white"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-semibold text-gray-900">{matiere.nom}</div>
                    <div className="text-xs text-gray-600">
                      {matiere.type} · Laize {matiere.laize} cm
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-500">{matiere.couleurs.length} coloris</div>
                    <div className="text-sm font-medium text-gray-800">{formatPriceTTC(matiere)} € TTC/ML</div>
                  </div>
                </div>
                {(matiere.favori || isFeatured) && (
                  <span className="inline-flex items-center gap-1 mt-3 text-xs font-medium px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                    {isFeatured ? "Sponsorisé" : "Favori"}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RecentMaterialsGrid;
